import React, { useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  CasinoHomeSection,
  CasinoHomeSectionHeader,
  CasinoHomeSectionHeaderLeft,
  CasinoHomeSectionSlider,
  GameListWrapper,
} from "./styled";
import { UserContext } from "../../Providers/UserContext";
import { HomeSectionGames } from "../Landing/styled";

import ogsGameList from "../../config/gameList.json";
import newgameList from "../../config/newGame.json";

type Category = {
  name: string;
  path?: string;
};

type Props = {
  category: Category;
};

type NormalizedGame = {
  key: string;
  title: string;
  imgSrc: string;
  onClick: () => void;
};

const RGS_PROVIDERS = new Set([
  "NETENT",
  "AMATIC",
  "NOVOMATIC",
  "AINSWORTH",
  "QUICKSPIN",
  "EGT",
  "PRAGMATIC",
  "EGT_EQ",
  "PATE",
  "MERKUR",
  "EGT_DIGITAL"
]);

const OGS_PROVIDERS = new Set([
  "NETENT2",
  "AMATIC2",
  "NOVOMATIC2",
  "ARISTOCRAT2",
  "EGT2",
  "Merkur",
  "PlayNGo",
  "PragmaticPlay",
  "River",
  "Scientific",
  "Yggdrasil",
  "Zeppelin",
]);

const imgStyle: React.CSSProperties = { borderRadius: 10 };

const GameCard: React.FC<{ title: string; imgSrc: string; onClick: () => void }> = ({
  title,
  imgSrc,
  onClick,
}) => (
  <HomeSectionGames onClick={onClick}>
    <div>
      <img src={imgSrc} alt={title} width="100%" height="100%" style={imgStyle} />
    </div>
    <span>{title}</span>
  </HomeSectionGames>
);

const GameList: React.FC<Props> = ({ category }) => {
  const navigate = useNavigate();
  const {
    netentGame,
    amaticGame,
    novomaticGame,
    quickspinGame,
    egtGame,
    ainsworthGame,
    egteqGame,
    pragmaticGame,
    pateGame,
    merkurGame,
    egtdigitalGame
  } = useContext(UserContext);

  const handleGamePlay = (games: any, provider: string, api?: "news" | "ogs") => {
    if (api === "news") {
      navigate(`/casino/play/${games.gameCode}`, {
        state: { gameItem: games, provider, api },
      });
      return;
    }

    if (api === "ogs") {
      if (provider === "EGT2" || provider === "ARISTOCRAT2") {
        navigate(`/casino/play/${games.gameId}`, {
          state: { gameItem: games, provider, api },
        });
      } else {
        navigate(`/casino/play/${games.gameName}`, {
          state: { gameItem: games, provider, api },
        });
      }
      return;
    }

    // RGS tuple [?, slug, title]
    navigate(`/casino/play/${games.gameCode}`, { state: { gameItem: games, provider } });
  };

  const rgsProviderMap: any = {
    NETENT: netentGame,
    AMATIC: amaticGame,
    NOVOMATIC: novomaticGame,
    AINSWORTH: ainsworthGame,
    QUICKSPIN: quickspinGame,
    EGT: egtGame,
    EGT_EQ: egteqGame,
    PRAGMATIC: pragmaticGame,
    PATE: pateGame,
    MERKUR: merkurGame,
    EGT_DIGITAL: egtdigitalGame,
  };

  const items: NormalizedGame[] = useMemo(() => {
    if (!category?.name) return [];

    // New Games section
    if (category.name === "New Games") {
      return newgameList.map((g: any, idx: number) => ({
        key: `${g.gameCode}-${idx}`,
        title: g.gameName,
        imgSrc: `/game-icon/${g.gameCode}.png`,
        onClick: () => handleGamePlay(g, category.name, "news"),
      }));
    }

    // RGS providers that use tuple data + CDN image path
    if (RGS_PROVIDERS.has(category.name)) {
      const arr = rgsProviderMap[category.name] || [];
      return arr.map((g: any, idx: number) => {
        const slug = g.gameCode;
        const title = g.name;
        const imgSrc = `https://back-office.b-cdn.net/icons/${g?.softwareProvider}/${g?.gameCode}.png`;
        return {
          key: `${slug}-${idx}`,
          title,
          imgSrc,
          onClick: () => handleGamePlay(g, category.name,),
        };
      });
    }

    // OGS providers that come from ogsGameList
    if (OGS_PROVIDERS.has(category.name)) {
      return (ogsGameList as any[])
        .filter((it) => it.provider === category.name)
        .map((g: any, idx: number) => ({
          key: `${g.gameId || g.gameName || g.displayName}-${idx}`,
          title: g.displayName,
          imgSrc: g.iconUrl,
          onClick: () => handleGamePlay(g, category.name, "ogs"),
        }));
    }

    return [];
  }, [
    category,
    netentGame,
    amaticGame,
    novomaticGame,
    quickspinGame,
    egtGame,
    ainsworthGame,
    egteqGame,
    pragmaticGame,
    pateGame,
    merkurGame,
    egtdigitalGame
  ]);

  return (
    <>
      <CasinoHomeSection>
        <CasinoHomeSectionHeader>
          <CasinoHomeSectionHeaderLeft>
            <span>{category?.name}</span>
          </CasinoHomeSectionHeaderLeft>
        </CasinoHomeSectionHeader>
        <CasinoHomeSectionSlider>
          <GameListWrapper>
            {items.map((it) => (
              <GameCard key={it.key} title={it.title} imgSrc={it.imgSrc} onClick={it.onClick} />
            ))}
          </GameListWrapper>
        </CasinoHomeSectionSlider>
      </CasinoHomeSection>
    </>
  );
};

export default GameList;
